/**
 * Four-step modal wizard: choose import source, provide data, pick/create pipeline, set rules/templates, start job.
 *
 * Where: triggered from stream detail when importing prospects into a pipeline.
 * Uses: `pipelineApi`, `jobApi`, `stepApi`; steps `ImportLeadsModalStep1`–`4`.
 * Behavior: coordinates stepper state, URL extraction for Sales Nav/Sheets, and background import job creation.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step as MuiStep,
  StepLabel,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pipelineApi, Step } from '@/api/pipeline';
import { jobApi } from '@/api/job';
import { stepApi } from '@/api/step';
import { Step1MethodSelection, ImportMethod } from './ImportLeadsModalStep1';
import { Step2InputData } from './ImportLeadsModalStep2';
import { Step3PipelineSelection } from './ImportLeadsModalStep3';
import { Step4ImportRules } from './ImportLeadsModalStep4';

interface ImportLeadsModalProps {
  open: boolean;
  onClose: () => void;
  streamUuid: string;
}

interface Step1Data {
  method: ImportMethod | null;
}

interface Step2Data {
  text?: string;
  file?: File;
  extractedUrls: string[];
  urlCount: number;
}

interface Step3Data {
  pipelineUuid: string | null;
  createNew: boolean;
  pipelineName: string;
  activeDays: string[];
  activeHoursFrom: number | null;
  activeHoursTo: number | null;
  templateUuid: string | null;
}

interface Step4Data {
  importRules: {
    dont_import_open_to_work: boolean;
    dont_import_less_that_500_connections: boolean;
    dont_import_no_photo_on_profile: boolean;
    dont_import_free_linkedin_account: boolean;
    dont_import_duplicate_prospects_with_same_owner: boolean;
    delete_other_waiting_prospects_with_same_owner: boolean;
  };
  startNow: boolean;
  startTime: Date | null;
  stepTemplates: Record<string, { message_template: string; ai_messaging: boolean }>;
}


export const ImportLeadsModal: React.FC<ImportLeadsModalProps> = ({ open, onClose, streamUuid }) => {
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(0);
  
  const [step1Data, setStep1Data] = useState<Step1Data>({ method: null });
  const [step2Data, setStep2Data] = useState<Step2Data>({ extractedUrls: [], urlCount: 0 });
  const [step3Data, setStep3Data] = useState<Step3Data>({
    pipelineUuid: null,
    createNew: false,
    pipelineName: '',
    activeDays: [],
    activeHoursFrom: null,
    activeHoursTo: null,
    templateUuid: null,
  });
  const [step4Data, setStep4Data] = useState<Step4Data>({
    importRules: {
      dont_import_open_to_work: false,
      dont_import_less_that_500_connections: false,
      dont_import_no_photo_on_profile: false,
      dont_import_free_linkedin_account: false,
      dont_import_duplicate_prospects_with_same_owner: true,
      delete_other_waiting_prospects_with_same_owner: false,
    },
    startNow: true,
    startTime: null,
    stepTemplates: {},
  });

  // Fetch pipeline steps when pipeline is selected/created
  const { data: pipelineStepsData } = useQuery({
    queryKey: ['steps', step3Data.pipelineUuid],
    queryFn: () =>
      pipelineApi.getSteps({
        pipeline_uuid: step3Data.pipelineUuid!,
        // This wizard intentionally works with a soft-deleted draft pipeline
        // between steps 3 and 4, so we must explicitly allow steps retrieval
        // for soft-deleted pipelines.
        include_deleted_pipeline: true,
      }),
    enabled: !!step3Data.pipelineUuid && (activeStep >= 3 || activeStep === 2),
  });

  const steps = pipelineStepsData?.output?.items || [];
  
  // Filter steps that have message_template (not null/undefined)
  const stepsWithTemplates: Step[] = steps
    .filter(
      (step: Step) =>
        step.message_template !== null && step.message_template !== undefined
    )
    .sort((a: Step, b: Step) => {
      const aOrder = a.sequence_order ?? Number.MAX_SAFE_INTEGER;
      const bOrder = b.sequence_order ?? Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      // Fallback for legacy steps without sequence_order.
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });

  const connectionRequestMaxLength = 300;

  const hasInvalidConnectionRequestMessageTemplate = (): boolean => {
    // Validate only for steps where action is SEND_CONNECTION_REQUEST (backend value: "Send Connection Request")
    for (const step of stepsWithTemplates) {
      if ((step.action || '') !== 'Send Connection Request') continue;
      const template = step4Data.stepTemplates[step.key]?.message_template ?? step.message_template ?? '';
      if ((template || '').length > connectionRequestMaxLength) return true;
    }
    return false;
  };

  // Initialize step templates in step4Data when steps are loaded
  useEffect(() => {
    if (stepsWithTemplates.length > 0 && step3Data.pipelineUuid) {
      const newStepTemplates: Record<string, { message_template: string; ai_messaging: boolean }> = {};
      stepsWithTemplates.forEach((step: Step) => {
        if (!step4Data.stepTemplates[step.key]) {
          newStepTemplates[step.key] = {
            message_template: step.message_template || '',
            ai_messaging: step.ai_messaging || false,
          };
        } else {
          newStepTemplates[step.key] = step4Data.stepTemplates[step.key];
        }
      });
      if (Object.keys(newStepTemplates).length > 0) {
        setStep4Data(prev => ({
          ...prev,
          stepTemplates: { ...prev.stepTemplates, ...newStepTemplates },
        }));
      }
    }
  }, [stepsWithTemplates.length, step3Data.pipelineUuid]);



  // Fetch pipelines for the stream (separate query key from infinite list tab)
  const { data: pipelinesData } = useQuery({
    queryKey: ['stream-pipelines', 'import-modal', streamUuid],
    queryFn: () => pipelineApi.getPipelines({ stream_uuid: streamUuid }),
    enabled: open && activeStep >= 2,
  });

  // Fetch template pipelines (only templates with is_template=true)
  const { data: templatesData } = useQuery({
    queryKey: ['pipelines', 'templates'],
    queryFn: () => pipelineApi.getPipelines({ is_template: true }),
    enabled: open && activeStep >= 2 && step3Data.createNew,
  });

  // Extract URLs mutation
  const extractUrlsMutation = useMutation({
    mutationFn: (data: { text?: string; file?: File }) => jobApi.extractUrls(data),
    onSuccess: (response) => {
      setStep2Data({
        ...step2Data,
        extractedUrls: response.output.urls,
        urlCount: response.output.count,
      });
    },
  });

  // Create pipeline mutation
  const createPipelineMutation = useMutation({
    mutationFn: (data: Parameters<typeof pipelineApi.createPipeline>[0]) => pipelineApi.createPipeline(data),
    onSuccess: (response) => {
      setStep3Data((prev) => ({ ...prev, pipelineUuid: response.output.key }));
    },
  });

  // Delete draft pipeline mutation (used when going back from step 4 to 3)
  const deletePipelineMutation = useMutation({
    mutationFn: (pipelineUuid: string) => pipelineApi.deletePipeline(pipelineUuid),
  });

  // Create job mutation
  const createJobMutation = useMutation({
    mutationFn: (data: Parameters<typeof jobApi.createJob>[0]) => jobApi.createJob(data),
    onSuccess: () => {
      // After successfully creating the job (and after pipeline has been restored
      // and configured), refresh jobs and pipelines for this stream so the
      // Pipelines tab reflects the newly created pipeline.
      queryClient.invalidateQueries({ queryKey: ['jobs', streamUuid] });
      queryClient.invalidateQueries({ queryKey: ['stream-pipelines'] });
      onClose();
      resetForm();
    },
  });

  const resetForm = () => {
    setActiveStep(0);
    setStep1Data({ method: null });
    setStep2Data({ extractedUrls: [], urlCount: 0 });
    setStep3Data({
      pipelineUuid: null,
      createNew: false,
      pipelineName: '',
      activeDays: [],
      activeHoursFrom: null,
      activeHoursTo: null,
      templateUuid: null,
    });
    setStep4Data({
      importRules: {
        dont_import_open_to_work: false,
        dont_import_less_that_500_connections: false,
        dont_import_no_photo_on_profile: false,
        dont_import_free_linkedin_account: false,
        dont_import_duplicate_prospects_with_same_owner: true,
        delete_other_waiting_prospects_with_same_owner: false,
      },
      startNow: true,
      startTime: null,
      stepTemplates: {},
    });
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const handleNext = async () => {
    if (activeStep === 0) {
      if (!step1Data.method) return;
      setActiveStep(1);
    } else if (activeStep === 1) {
      if (step2Data.urlCount === 0) return;
      setActiveStep(2);
    } else if (activeStep === 2) {
      if (step3Data.createNew) {
        if (!step3Data.pipelineName.trim() || 
            !step3Data.templateUuid || 
            step3Data.activeHoursFrom === null || 
            step3Data.activeHoursTo === null) return;
        // If pipeline already exists (e.g. user went back from step 4),
        // don't create it again – just proceed.
        if (step3Data.pipelineUuid) {
          setActiveStep(3);
          return;
        }
        // Create pipeline first (without import rules, they'll be set in step 4)
        try {
          const pipelineData: Parameters<typeof pipelineApi.createPipeline>[0] = {
            stream_uuid: streamUuid,
            name: step3Data.pipelineName,
            active_days: step3Data.activeDays.length > 0 ? step3Data.activeDays : undefined,
            active_hours_from: step3Data.activeHoursFrom ?? undefined,
            active_hours_to: step3Data.activeHoursTo ?? undefined,
            template_uuid: step3Data.templateUuid || undefined,
            // Create as a soft-deleted draft so it doesn't appear in lists
            // until the user confirms in step 4.
            mark_deleted: true,
          };
          const response = await createPipelineMutation.mutateAsync(pipelineData);
          // Update state and immediately proceed to next step
          const newPipelineUuid = response.output.key;
          setStep3Data((prev) => ({ ...prev, pipelineUuid: newPipelineUuid }));
          // Proceed to next step after pipeline is created
          setActiveStep(3);
          return;
        } catch (error) {
          return; // Error will be shown by mutation
        }
      }
      if (!step3Data.pipelineUuid) return;
      setActiveStep(3);
    } else if (activeStep === 3) {
      handleSubmit();
    }
  };

  const handleBack = async () => {
    // When going back from step 4 to step 3 for a newly created pipeline,
    // we need to completely remove the draft pipeline (and its relations)
    // from the database so that a fresh one is created on the next "Next".
    if (activeStep === 3 && step3Data.createNew && step3Data.pipelineUuid) {
      try {
        await deletePipelineMutation.mutateAsync(step3Data.pipelineUuid);
      } catch (error) {
        // Log and continue - the draft might have already been removed
        console.error('Failed to delete draft pipeline:', error);
      }

      // Clear the pipeline UUID so the next "Next" from step 3 recreates it
      setStep3Data((prev) => ({
        ...prev,
        pipelineUuid: null,
      }));

      // Clear step templates so we don't keep templates for deleted steps
      setStep4Data((prev) => ({
        ...prev,
        stepTemplates: {},
      }));

      setActiveStep(2);
      return;
    }

    setActiveStep(activeStep - 1);
  };

  const handleSubmit = async () => {
    if (!step3Data.pipelineUuid || step2Data.extractedUrls.length === 0) return;

    // Update pipeline with import rules first and restore soft-deleted draft
    if (step3Data.pipelineUuid) {
      try {
        await pipelineApi.updatePipeline(step3Data.pipelineUuid, {
          import_rules: step4Data.importRules,
          restore_deleted: true,
        });
      } catch (error) {
        console.error('Failed to update pipeline import rules:', error);
        // Continue anyway
      }
    }

    // Update step message templates and ai_messaging
    const stepUpdatePromises = Object.entries(step4Data.stepTemplates).map(([stepUuid, templateData]) => {
      return stepApi.updateStep(stepUuid, {
        message_template: templateData.message_template || null,
        ai_messaging: templateData.ai_messaging,
      });
    });

    try {
      await Promise.all(stepUpdatePromises);
    } catch (error) {
      console.error('Failed to update step templates:', error);
      // Continue anyway
    }

    const jobData: Parameters<typeof jobApi.createJob>[0] = {
      stream_uuid: streamUuid,
      pipeline_uuid: step3Data.pipelineUuid,
      urls: step2Data.extractedUrls,
    };

    if (!step4Data.startNow && step4Data.startTime) {
      jobData.available_at = step4Data.startTime.toISOString();
    }

    createJobMutation.mutate(jobData);
  };

  const handleExtractUrls = () => {
    if (step1Data.method === 'text' && step2Data.text && step2Data.text.trim()) {
      extractUrlsMutation.mutate({ text: step2Data.text });
    }
  };

  const renderStep1 = () => (
    <Step1MethodSelection
      method={step1Data.method}
      onMethodChange={(method) => setStep1Data({ method })}
    />
  );

  const renderStep2 = () => (
    <Step2InputData
      method={step1Data.method}
      text={step2Data.text}
      file={step2Data.file}
      urlCount={step2Data.urlCount}
      isExtracting={extractUrlsMutation.isPending}
      extractionError={extractUrlsMutation.error as Error | null}
      onTextChange={(text) => setStep2Data({ ...step2Data, text })}
      onFileChange={(file) => {
        setStep2Data({ ...step2Data, file });
        extractUrlsMutation.mutate({ file });
      }}
      onFileRemove={() => setStep2Data({ ...step2Data, file: undefined, extractedUrls: [], urlCount: 0 })}
      onExtractUrls={handleExtractUrls}
    />
  );

  const renderStep3 = () => (
    <Step3PipelineSelection
      createNew={step3Data.createNew}
      pipelineUuid={step3Data.pipelineUuid}
      pipelineName={step3Data.pipelineName}
      activeDays={step3Data.activeDays}
      activeHoursFrom={step3Data.activeHoursFrom}
      activeHoursTo={step3Data.activeHoursTo}
      templateUuid={step3Data.templateUuid}
      pipelines={pipelinesData?.output?.items || []}
      templates={templatesData?.output?.items || []}
      onCreateNewChange={(createNew) => setStep3Data({ ...step3Data, createNew, pipelineUuid: null })}
      onPipelineUuidChange={(uuid) => setStep3Data({ ...step3Data, pipelineUuid: uuid })}
      onPipelineNameChange={(name) => setStep3Data({ ...step3Data, pipelineName: name })}
      onActiveDaysChange={(days) => setStep3Data({ ...step3Data, activeDays: days })}
      onActiveHoursFromChange={(hour) => setStep3Data({ ...step3Data, activeHoursFrom: hour })}
      onActiveHoursToChange={(hour) => setStep3Data({ ...step3Data, activeHoursTo: hour })}
      onTemplateUuidChange={(uuid) => setStep3Data({ ...step3Data, templateUuid: uuid })}
    />
  );

  const renderStep4 = () => {
    const handleStepTemplateChange = (stepUuid: string, template: { message_template: string; ai_messaging: boolean }) => {
      setStep4Data(prev => ({
        ...prev,
        stepTemplates: {
          ...prev.stepTemplates,
          [stepUuid]: template,
        },
      }));
    };

    return (
      <Step4ImportRules
        importRules={step4Data.importRules}
        startNow={step4Data.startNow}
        startTime={step4Data.startTime}
        stepTemplates={step4Data.stepTemplates}
        stepsWithTemplates={stepsWithTemplates}
        onImportRulesChange={(rules) => setStep4Data(prev => ({ ...prev, importRules: rules }))}
        onStartNowChange={(startNow) => setStep4Data(prev => ({ ...prev, startNow }))}
        onStartTimeChange={(time) => setStep4Data(prev => ({ ...prev, startTime: time }))}
        onStepTemplateChange={handleStepTemplateChange}
      />
    );
  };

  const canProceed = () => {
    if (activeStep === 0) return step1Data.method !== null;
    if (activeStep === 1) return step2Data.urlCount > 0;
    if (activeStep === 2) {
      if (step3Data.createNew) {
        return step3Data.pipelineName.trim() !== '' && 
               step3Data.templateUuid !== null &&
               step3Data.activeHoursFrom !== null &&
               step3Data.activeHoursTo !== null;
      }
      return step3Data.pipelineUuid !== null;
    }
    if (activeStep === 3) {
      const { dont_import_duplicate_prospects_with_same_owner, delete_other_waiting_prospects_with_same_owner } = step4Data.importRules;
      const exactlyOneDuplicateOption = dont_import_duplicate_prospects_with_same_owner !== delete_other_waiting_prospects_with_same_owner;
      return !hasInvalidConnectionRequestMessageTemplate() && exactlyOneDuplicateOption;
    }
    return true;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Import Leads</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4, mt: 2 }}>
          <MuiStep>
            <StepLabel>Select Method</StepLabel>
          </MuiStep>
          <MuiStep>
            <StepLabel>Input Data</StepLabel>
          </MuiStep>
          <MuiStep>
            <StepLabel>Select Pipeline</StepLabel>
          </MuiStep>
          <MuiStep>
            <StepLabel>Import Rules</StepLabel>
          </MuiStep>
        </Stepper>

        {activeStep === 0 && renderStep1()}
        {activeStep === 1 && renderStep2()}
        {activeStep === 2 && renderStep3()}
        {activeStep === 3 && renderStep4()}

        {createJobMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {createJobMutation.error instanceof Error ? createJobMutation.error.message : 'Failed to create job'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {activeStep > 0 && <Button onClick={handleBack}>Back</Button>}
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!canProceed() || createJobMutation.isPending || createPipelineMutation.isPending}
          startIcon={
            (createJobMutation.isPending || createPipelineMutation.isPending) ? (
              <CircularProgress size={20} />
            ) : undefined
          }
        >
          {activeStep === 3 ? 'Import' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

